import React from "react";
import { FileText, FileImage, FileVideo, File, Download } from "lucide-react";
import { getFileNameFromUrl } from "@/modules/hub/features/ResourceLibrary/utils/contentBlockUtils";

interface FileEmbedProps {
  title: string | null;
  contentUrl: string;
  /**
   * Presigned URL that saves the file under its original name. The public
   * contentUrl still drives previews; a download through it would be saved
   * as the S3 object key (a hash).
   */
  downloadUrl?: string | null;
  bypassIframeForPdf?: boolean;
  contentType?: string | null;
  fileSize?: number | null;
  description?: string | null;
}

export function FileEmbed({
  title,
  contentUrl,
  downloadUrl,
  bypassIframeForPdf = false,
  contentType,
  fileSize,
  description,
}: FileEmbedProps) {
  const fileName = title ?? getFileNameFromUrl(contentUrl.trim());

  // Determine file icon based on mime type or file extension
  const getFileIcon = () => {
    if (contentType) {
      if (contentType.startsWith("image/")) return FileImage;
      if (contentType.startsWith("video/")) return FileVideo;
      if (contentType === "application/pdf") return FileText;
    }

    const lowerUrl = contentUrl.toLowerCase();
    if (lowerUrl.endsWith(".pdf")) return FileText;
    if (lowerUrl.match(/\.(jpg|jpeg|png|gif|webp|svg)$/)) return FileImage;
    if (lowerUrl.match(/\.(mp4|webm|mov|avi)$/)) return FileVideo;

    return File;
  };

  const FileIcon = getFileIcon();

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  if (contentUrl.toLowerCase().endsWith(".pdf") && !bypassIframeForPdf) {
    return (
      <div className="bg-gray-100 dark:bg-slate-800 rounded-lg overflow-hidden my-6">
        <iframe
          className="w-full border-0"
          height="600"
          src={`https://docs.google.com/viewer?url=${encodeURIComponent(
            contentUrl,
          )}&embedded=true`}
          width="100%"
        />
      </div>
    );
  }

  return (
    <div className="my-6">
      {contentUrl ? (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
          <div className="p-6">
            <div className="flex items-start gap-4">
              {/* Icon Container */}
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 flex items-center justify-center">
                  <FileIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>

              {/* Content Container */}
              <div className="flex-1 min-w-0">
                {title && (
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    {title}
                  </h3>
                )}
                {description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
                    {description}
                  </p>
                )}
                <div className="flex items-center gap-4">
                  {fileSize && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      File size: {formatFileSize(fileSize)}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Download Button */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
              <a
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium text-sm rounded-lg transition-all duration-200 shadow-sm hover:shadow"
                download
                href={downloadUrl ?? contentUrl}
                rel="noopener noreferrer"
                target="_blank"
              >
                <Download className="w-4 h-4" />
                Download File
              </a>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-gray-500 dark:text-gray-400">No file provided.</p>
      )}
    </div>
  );
}
